import React from "react";
import Link from "next/link";
import {
  buildAnniversaryQuizPath,
  trackAnniversaryEvent,
} from "@/utils/anniversary";
import {
  buildMidYearQuizPath,
  isMidYearPromotionVisible,
  trackMidYearEvent,
} from "@/utils/mid-year-career-upgrade";
import {
  buildSummerQuizPath,
  isSummerSponsorshipPromotionVisible,
  trackSummerSponsorshipEvent,
} from "@/utils/summer-sponsorship-2026";
import { PATHWAY_SELECTOR_PAGE_PATH } from "@/utils/pathway-selector";

interface QuizPromoProps {
  title?: string;
  description?: string;
  variant?: "default" | "compact" | "banner";
  campaign?: "default" | "anniversary" | "mid-year" | "summer" | "active";
}


function resolveActiveQuizCampaign(
  campaign: QuizPromoProps["campaign"],
): "default" | "anniversary" | "mid-year" | "summer" {
  if (campaign !== "active") {
    return campaign || "default";
  }

  if (isMidYearPromotionVisible()) {
    return "mid-year";
  }

  if (isSummerSponsorshipPromotionVisible()) {
    return "summer";
  }

  return "default";
}

export default function QuizPromo({
  title,
  description,
  variant = "default",
  campaign = "default",
}: QuizPromoProps) {
  // Resolve during render so campaign copy is SSR-stable and does not
  // swap after hydration (layout shift on landing pages).
  const resolvedCampaign = resolveActiveQuizCampaign(campaign);

  const isAnniversary = resolvedCampaign === "anniversary";
  const isMidYear = resolvedCampaign === "mid-year";
  const isSummer = resolvedCampaign === "summer";
  const isCampaignPromo = isMidYear || isSummer || isAnniversary;
  const href = isSummer
    ? buildSummerQuizPath()
    : isMidYear
      ? buildMidYearQuizPath()
      : isAnniversary
        ? buildAnniversaryQuizPath()
        : "/best-aesthetic-course-for-you";
  const resolvedTitle =
    title ||
    (isSummer || isMidYear
      ? "Not sure which programme fits your goals?"
      : isAnniversary
        ? "Not sure which course is right for you?"
        : "Find Your Ideal Training Path");
  const resolvedDescription =
    description ||
    (isSummer || isMidYear
      ? "Take our quick quiz for a personalised training recommendation."
      : isAnniversary
        ? "Take our quick quiz for a personalised course recommendation."
        : "Take our quick quiz to discover the perfect course for your career goals");

  const handleClick = () => {
    if (isSummer) {
      trackSummerSponsorshipEvent("summer_quiz_start", {
        placement: `quiz-promo-${variant}`,
      });
      return;
    }

    if (isMidYear) {
      trackMidYearEvent("mid_year_quiz_start", {
        placement: `quiz-promo-${variant}`,
      });
      return;
    }

    if (isAnniversary) {
      trackAnniversaryEvent("anniversary_quiz_start", {
        placement: `quiz-promo-${variant}`,
      });
    }
  };

  const ctaLabel = isCampaignPromo ? "Start Quiz" : "Take Quiz";
  const showPathwayCta = !isCampaignPromo;
  const promoGradient = isSummer
    ? "rounded-lg bg-gradient-to-r from-summer-600 to-summer-700 p-4 text-white"
    : isMidYear
      ? "rounded-lg bg-gradient-to-r from-midyear-600 to-midyear-700 p-4 text-white"
      : "rounded-lg bg-gradient-to-r from-brand-600 to-brand-700 p-4 text-white";
  const promoBannerGradient = isSummer
    ? "rounded-lg bg-gradient-to-r from-summer-600 to-summer-800 px-4 py-6 text-white"
    : isMidYear
      ? "rounded-lg bg-gradient-to-r from-midyear-600 to-midyear-800 px-4 py-6 text-white"
      : "rounded-lg bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-6 text-white";
  const promoCtaClass = isSummer
    ? "rounded-md bg-white px-4 py-2 text-center text-sm font-semibold text-summer-600 transition-colors hover:bg-gray-100"
    : isMidYear
      ? "rounded-md bg-white px-4 py-2 text-center text-sm font-semibold text-midyear-600 transition-colors hover:bg-gray-100"
      : "rounded-md bg-white px-4 py-2 text-center text-sm font-semibold text-brand-600 transition-colors hover:bg-gray-100";
  const promoBannerCtaClass = isSummer
    ? "inline-block rounded-md bg-white px-6 py-3 font-semibold text-summer-700 transition-colors hover:bg-summer-50"
    : isMidYear
      ? "inline-block rounded-md bg-white px-6 py-3 font-semibold text-midyear-700 transition-colors hover:bg-midyear-50"
      : "inline-block rounded-md bg-white px-6 py-3 font-semibold text-brand-600 transition-colors hover:bg-gray-100";
  const promoCardClass = isSummer
    ? "rounded-lg border border-summer-200 bg-gradient-to-br from-summer-50 to-summer-100 p-6"
    : isMidYear
      ? "rounded-lg border border-midyear-200 bg-gradient-to-br from-midyear-50 to-midyear-100 p-6"
      : "rounded-lg border border-brand-200 bg-gradient-to-br from-brand-50 to-brand-100 p-6";
  const promoIconClass = isSummer
    ? "mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-summer-600"
    : isMidYear
      ? "mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-midyear-600"
      : "mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-600";
  const promoButtonClass = isSummer
    ? "inline-block rounded-md bg-summer-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-summer-700"
    : isMidYear
      ? "inline-block rounded-md bg-midyear-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-midyear-700"
      : "inline-block rounded-md bg-brand-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-brand-700";

  if (variant === "compact") {
    return (
      <div className={promoGradient}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold">{resolvedTitle}</h3>
            <p className="mt-1 text-xs opacity-90">{resolvedDescription}</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0">
            {showPathwayCta ? (
              <Link
                href={PATHWAY_SELECTOR_PAGE_PATH}
                className="rounded-md border border-white/80 bg-white/10 px-4 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-white/20"
              >
                Find Your Training Route
              </Link>
            ) : null}
            <Link
              href={href}
              onClick={handleClick}
              className={promoCtaClass}
            >
              {ctaLabel}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "banner") {
    return (
      <div className={promoBannerGradient}>
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-2 text-2xl font-bold">{resolvedTitle}</h2>
          <p className="mb-4 text-lg opacity-90">{resolvedDescription}</p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            {showPathwayCta ? (
              <Link
                href={PATHWAY_SELECTOR_PAGE_PATH}
                className="inline-block rounded-md border border-white/80 bg-white/10 px-6 py-3 font-semibold text-white transition-colors hover:bg-white/20"
              >
                Find Your Training Route
              </Link>
            ) : null}
            <Link
              href={href}
              onClick={handleClick}
              className={promoBannerCtaClass}
            >
              {isCampaignPromo ? "Start Quiz" : "Start Quiz Now"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={promoCardClass}>
      <div className="text-center">
        <div className={promoIconClass}>
          <svg
            className="h-8 w-8 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-semibold text-gray-900">
          {resolvedTitle}
        </h3>
        <p className="mb-4 text-gray-600">{resolvedDescription}</p>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            href={href}
            onClick={handleClick}
            className={promoButtonClass}
          >
            {isCampaignPromo ? "Start the Quiz" : "Take the Quiz"}
          </Link>
          {showPathwayCta ? (
            <Link
              href={PATHWAY_SELECTOR_PAGE_PATH}
              className="inline-block rounded-md border border-brand-300 bg-white px-6 py-3 font-semibold text-brand-700 transition-colors hover:border-brand-500 hover:text-brand-800"
            >
              Find Your Training Route
            </Link>
          ) : null}
        </div>
        <p className="mt-3 text-sm text-gray-500">
          {isCampaignPromo
            ? "Get a personalised recommendation"
            : "Get a personalised recommendation + £50 voucher"}
        </p>
      </div>
    </div>
  );
}
